import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function SchoolPicker() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [entering, setEntering] = useState(null);
  const [form, setForm] = useState({
    name: '', slug: '', domain: '',
    admin_email: '', admin_password: '',
    admin_first_name: 'Admin', admin_last_name: 'User',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { enterSchool } = useAuth();

  const superName = localStorage.getItem('super_admin_name') || 'Super Admin';

  const loadSchools = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/super-admin/schools');
      setSchools(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('is_super_admin') !== 'true') {
      navigate('/login');
      return;
    }
    loadSchools();
  }, []);

  const handleEnterSchool = async (s) => {
    setEntering(s.id);
    try {
      await enterSchool(s.id);
    } catch (err) {
      alert('Failed to enter school: ' + (err.response?.data?.detail || err.message));
      setEntering(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      const res = await api.post('/super-admin/schools', form);
      setShowCreate(false);
      setForm({
        name: '', slug: '', domain: '',
        admin_email: '', admin_password: '',
        admin_first_name: 'Admin', admin_last_name: 'User',
      });
      await loadSchools();
      alert(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create school');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('master_token');
    localStorage.removeItem('is_super_admin');
    localStorage.removeItem('super_admin_name');
    localStorage.removeItem('active_school_id');
    localStorage.removeItem('active_school_name');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              <i className="fas fa-school mr-2"></i> Select a School
            </h1>
            <p className="text-slate-300 text-sm mt-1">
              Signed in as <strong>{superName}</strong>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreate(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <i className="fas fa-plus mr-1"></i> Create School
            </button>
            <button
              onClick={handleLogout}
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <i className="fas fa-sign-out-alt mr-1"></i> Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-400 text-red-100 text-sm">
            <i className="fas fa-exclamation-circle mr-1"></i> {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-white">
            <i className="fas fa-spinner fa-spin text-3xl"></i>
          </div>
        ) : schools.length === 0 ? (
          <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-12 text-center text-white">
            <i className="fas fa-school text-5xl mb-3 opacity-60"></i>
            <p className="text-lg">No schools yet</p>
            <p className="text-sm text-slate-300 mt-1">
              Click "Create School" to add your first one.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {schools.map((s) => (
              <button
                key={s.id}
                onClick={() => handleEnterSchool(s)}
                disabled={entering === s.id}
                className="bg-white rounded-xl p-5 text-left hover:shadow-2xl transition-all hover:-translate-y-0.5 shadow-lg disabled:opacity-60 disabled:cursor-wait"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-school text-lg"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-800 truncate">{s.name}</h3>
                    <p className="text-xs text-gray-500 truncate">@{s.domain}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="font-mono truncate">{s.admin_email}</span>
                  <span className="text-blue-600 font-semibold whitespace-nowrap ml-2">
                    {entering === s.id ? (
                      <><i className="fas fa-spinner fa-spin mr-1"></i> Entering…</>
                    ) : (
                      <>Enter <i className="fas fa-arrow-right ml-1"></i></>
                    )}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold">
                <i className="fas fa-plus-circle text-emerald-600 mr-2"></i> Create New School
              </h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 text-2xl leading-none">&times;</button>
            </div>

            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">School Name *</label>
                <input
                  className="w-full p-2 border rounded"
                  placeholder="e.g. Greenwood High School"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Slug *</label>
                  <input
                    className="w-full p-2 border rounded"
                    placeholder="greenwood"
                    value={form.slug}
                    onChange={(e) =>
                      setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Domain *</label>
                  <input
                    className="w-full p-2 border rounded"
                    placeholder="greenwood.com"
                    value={form.domain}
                    onChange={(e) => setForm({ ...form, domain: e.target.value.toLowerCase() })}
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Initial Admin Account
                  <span className="ml-1 text-gray-400 font-normal normal-case">
                    (optional — leave password blank to manage via super admin)
                  </span>
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input
                    className="p-2 border rounded"
                    placeholder="First Name"
                    value={form.admin_first_name}
                    onChange={(e) => setForm({ ...form, admin_first_name: e.target.value })}
                  />
                  <input
                    className="p-2 border rounded"
                    placeholder="Last Name"
                    value={form.admin_last_name}
                    onChange={(e) => setForm({ ...form, admin_last_name: e.target.value })}
                  />
                </div>
                <input
                  type="email"
                  className="w-full p-2 border rounded mb-3"
                  placeholder={`admin@${form.domain || 'school.com'}`}
                  value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  required
                />
                <input
                  type="password"
                  className="w-full p-2 border rounded"
                  placeholder="Password (min 8 chars — optional)"
                  value={form.admin_password}
                  onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-sm disabled:opacity-50"
                >
                  {creating ? 'Creating…' : 'Create School'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}